import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { TCompanyForm } from "@/features/company/types/CompanyForm";

/** Business classification fields that pair with a specific-industry selection. */
type TBusinessClassificationField =
  | "companySpecificBusinessClassificationIds1"
  | "companySpecificBusinessClassificationIds2"
  | "companySpecificBusinessClassificationIds3";

/**
 * Keeps a 業務区分 (business classification) multi-select field in sync with the industry selected in the same row:
 * - options are derived from the selected industry, minus values already chosen in other rows (`excludeValues`)
 *   so the same classification can't be picked twice — this row's own selected values are always kept visible;
 * - if the industry has exactly one classification, it is auto-selected and the field is shown as readonly
 *   (value assigned & visible, no interaction) — not disabled;
 * - the field is disabled only while no industry is selected;
 * - any selected value that no longer belongs to the selected industry (industry changed/cleared) is dropped.
 *
 * Auto-select & readonly use the industry's full classification list, not the deduped one, so excluding a
 * cross-row value never collapses the row into a fake "single option".
 *
 * Values are compared as strings: the form stores string ids matching the master option values, and they are
 * converted back to number in convertCompanyFormDataReq before submit.
 */
export const useBusinessClassificationSync = (
  industryId: number | null | undefined,
  fieldName: TBusinessClassificationField,
  excludeValues: (string | number | null | undefined)[] = [],
) => {
  const { control, setValue } = useFormContext<TCompanyForm>();
  const { getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry, isLoading } = useGetCompanySpecificIndustryList();
  const selectedValues = useWatch({ control, name: fieldName });

  // Full classification list for the selected industry (drives auto-select / readonly / membership).
  const industryOptions = useMemo(
    () => (industryId ? getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(industryId) : []),
    [industryId, getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry],
  );

  useEffect(() => {
    if (isLoading) return;
    const current = Array.isArray(selectedValues) ? selectedValues.map(String) : [];
    if (!industryId) {
      if (current.length) setValue(fieldName, []);
      return;
    }
    if (industryOptions.length === 1) {
      const onlyValue = String(industryOptions[0].value);
      if (current.length !== 1 || current[0] !== onlyValue) setValue(fieldName, [onlyValue]);
      return;
    }
    // Drop selected values that no longer belong to the selected industry (industry changed).
    const validValues = current.filter((v) => industryOptions.some((opt) => String(opt.value) === v));
    if (validValues.length !== current.length) setValue(fieldName, validValues);
  }, [isLoading, selectedValues, industryOptions, industryId, setValue, fieldName]);

  // Rendered options: drop classifications already chosen in other rows, but never hide this row's own values.
  const options = useMemo(() => {
    const selected = Array.isArray(selectedValues) ? selectedValues.map(String) : [];
    return industryOptions.filter(
      (opt) =>
        selected.includes(String(opt.value)) ||
        !excludeValues.some((excluded) => excluded != null && String(excluded) === String(opt.value)),
    );
  }, [industryOptions, excludeValues, selectedValues]);

  return { options, disabled: !industryId, readonly: industryOptions.length === 1 };
};
